import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import { useCreateTreeStructure } from './hooks'
import { useAtom, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  lessonAssociationsAtom,
  treeStructureFlowIdAtom,
  treeStructureResultAtom,
} from './atoms'
import { TreeStructureResponse } from 'components/contentService/types'
import { FlowIdView } from 'components/contentService/JobView'
import { ExportToGoogleSheetsButton } from './ExportToGoogleSheetsButton'
import { LessonSummary } from './LessonSummary'
import {
  LessonAssociation,
  LessonEntry,
} from 'domains/scope-and-sequence/types'
import { convertApiResponseToLessonCentric } from './convertFromApi'

export function TreeStructureDetails() {
  const [flowId, setFlowId] = useAtom(treeStructureFlowIdAtom)
  const setResult = useSetAtom(treeStructureResultAtom)
  const { canCreate, doCreate } = useCreateTreeStructure()

  const onResult = useCallback(
    (result: TreeStructureResponse) => {
      setResult(convertApiResponseToLessonCentric(result.data))
    },
    [setResult],
  )

  if (!canCreate) {
    return (
      <Box padding="5px" color="text.secondary">
        Once you have imported your pacing guide, selected a crosswalk, and
        added at least one source tree, you&apos;ll be able to export the Scope
        and Sequence, make edits in Google Sheets, and upload to create your
        course.
      </Box>
    )
  }
  return (
    <Box>
      <Button disabled={!!flowId} onClick={doCreate}>
        Create Tree Structure
      </Button>
      {flowId && <Button onClick={() => setFlowId('')}>Reset</Button>}
      <FlowIdView
        flowId={flowId}
        onFlowResult={onResult}
        FlowResultComponent={ResultsView}
      />
    </Box>
  )
}

function ResultsView({
  flowResult,
}: {
  flowResult: TreeStructureResponse | undefined
}) {
  const [lessonAssociations, setLessonAssociations] = useAtom(
    lessonAssociationsAtom,
  )
  const setTreeStructureResult = useSetAtom(treeStructureResultAtom)

  const lessonCentricData = useMemo(() => {
    if (!flowResult?.data) return null
    return convertApiResponseToLessonCentric(flowResult.data)
  }, [flowResult])

  // Initialize associations from flow result
  useEffect(() => {
    if (!lessonCentricData) return

    const newAssociations: Record<string, LessonAssociation> = {}
    lessonCentricData.lessons.forEach((lesson) => {
      // Only set if not already exists (preserve user edits)
      if (!lessonAssociations[lesson.key]) {
        newAssociations[lesson.key] = {
          lessonKey: lesson.key,
          targetUnit: null, // Start unassigned - user will assign via dropdown
          targetSection: '',
          targetLessonNumber: null,
        }
      }
    })

    if (Object.keys(newAssociations).length > 0) {
      setLessonAssociations((prev) => ({ ...prev, ...newAssociations }))
    }

    // Store the result
    setTreeStructureResult(lessonCentricData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonCentricData, setLessonAssociations, setTreeStructureResult])

  // Partition lessons into assigned and pool based on associations
  const { assignedLessons, poolLessons } = useMemo(() => {
    if (!lessonCentricData) return { assignedLessons: [], poolLessons: [] }

    const assigned: LessonEntry[] = []
    const pool: LessonEntry[] = []

    lessonCentricData.lessons.forEach((lesson) => {
      const assoc = lessonAssociations[lesson.key]
      if (assoc?.targetUnit != null) {
        assigned.push(lesson)
      } else {
        pool.push(lesson)
      }
    })

    return { assignedLessons: assigned, poolLessons: pool }
  }, [lessonCentricData, lessonAssociations])

  // Build unit options for dropdown
  const unitOptions = useMemo(() => {
    if (!lessonCentricData) return []
    return lessonCentricData.units.map((u) => ({
      value: u.number,
      label: u.name,
    }))
  }, [lessonCentricData])

  if (!flowResult || !lessonCentricData) return null

  return (
    <LessonSummary
      assignedLessons={assignedLessons}
      poolLessons={poolLessons}
      units={lessonCentricData.units}
      unitOptions={unitOptions}
      courseToolbarItems={[<ExportToGoogleSheetsButton key="export" />]}
    />
  )
}
